// ═══════════════════════════════════════════════════════════════
// COMPONENTE: Formulario de Contrato
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import {
  FileText,
  Save,
  X,
  Upload,
  User,
  Calendar,
  DollarSign,
  Briefcase,
  Building2,
  Clock,
  AlertCircle
} from 'lucide-react';
import { useContracts } from '../../../hooks/useContracts';
import { useDepartments } from '../../../hooks/useDepartments';
import { usePositions } from '../../../hooks/usePositions';
import api from '../../../api/config';

const ContractForm = ({ contract, employees, onClose }) => {
  const { createContract, updateContract, loading } = useContracts();
  const { departments, loadDepartments } = useDepartments();
  const { positions, loadPositions } = usePositions();

  const isEditing = !!contract;

  const [formData, setFormData] = useState({
    employee_id: '',
    tipo_contrato: 'indefinido',
    numero_contrato: '',
    fecha_inicio: '',
    fecha_fin: '',
    position_id: '',
    department_id: '',
    salario: '',
    moneda: 'DOP',
    tipo_jornada: 'completa',
    horas_semanales: 44,
    tiene_periodo_prueba: false,
    duracion_prueba_dias: '',
    fecha_fin_prueba: '',
    notas: '',
    es_renovacion: false,
    contrato_anterior_id: '',
    shift_id: '',
    horario_entrada: '',
    horario_salida: '',
    dias_laborales: []
  });

  const [documento, setDocumento] = useState(null);
  const [errors, setErrors] = useState({});
  const [employeeContracts, setEmployeeContracts] = useState([]);
  const [availableShifts, setAvailableShifts] = useState([]);
  const [orgUnits, setOrgUnits] = useState([]);

  useEffect(() => {
    loadDepartments();
    loadPositions();
    loadShifts();
    api.get('/organizational-units').then(r => setOrgUnits(r.data || [])).catch(() => {});
  }, []);

  const loadShifts = async () => {
    try {
      const [shiftsRes, schedulesRes] = await Promise.all([
        api.get('/shifts'),
        api.get('/schedules')
      ]);

      const shifts = shiftsRes.data.shifts || [];
      const schedules = schedulesRes.data.schedules || [];

      // Mapear shifts a la estructura que espera el formulario
      const mappedShifts = shifts.map(shift => {
        // Obtener el primer horario no nulo de la semana como representativo
        const horarios = shift.horarios_semana || {};
        const dias = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];

        let firstScheduleId = null;
        const diasConHorario = [];

        dias.forEach(dia => {
          if (horarios[dia]) {
            if (!firstScheduleId) {
              firstScheduleId = horarios[dia];
            }
            diasConHorario.push(dia);
          }
        });

        const schedule = schedules.find(s => s.id === firstScheduleId);

        return {
          id: shift.id,
          nombre: shift.nombre,
          horaInicio: schedule?.hora_inicio || '09:00',
          horaFin: schedule?.hora_fin || '18:00',
          diasLaborales: diasConHorario,
          ciclo_semanas: shift.ciclo_semanas,
          horarios_semana: shift.horarios_semana
        };
      });

      setAvailableShifts(mappedShifts);
    } catch (error) {
      console.error('Error loading shifts:', error);
    }
  };

  useEffect(() => {
    if (contract) {
      console.log('📅 ContractForm - Contrato recibido:', {
        fecha_inicio: contract.fecha_inicio,
        fecha_fin: contract.fecha_fin,
        fecha_fin_prueba: contract.fecha_fin_prueba,
        shift_id: contract.shift_id
      });

      const initialData = {
        employee_id: contract.employee_id || '',
        tipo_contrato: contract.tipo_contrato || 'indefinido',
        numero_contrato: contract.numero_contrato || '',
        fecha_inicio: contract.fecha_inicio || '',
        fecha_fin: contract.fecha_fin || '',
        position_id: contract.position_id || '',
        department_id: contract.department_id || '',
        salario: contract.salario || '',
        moneda: contract.moneda || 'DOP',
        tipo_jornada: contract.tipo_jornada || 'completa',
        horas_semanales: contract.horas_semanales || 44,
        tiene_periodo_prueba: contract.tiene_periodo_prueba || false,
        duracion_prueba_dias: contract.duracion_prueba_dias || '',
        fecha_fin_prueba: contract.fecha_fin_prueba || '',
        notas: contract.notas || '',
        es_renovacion: false,
        contrato_anterior_id: '',
        shift_id: contract.shift_id || '',
        horario_entrada: contract.horario_entrada || '',
        horario_salida: contract.horario_salida || '',
        dias_laborales: contract.dias_laborales || []
      };

      // Si tiene shift_id, auto-completar con los datos del turno
      if (contract.shift_id && availableShifts.length > 0) {
        const selectedShift = availableShifts.find(s => s.id === contract.shift_id);
        if (selectedShift) {
          initialData.horario_entrada = selectedShift.horaInicio;
          initialData.horario_salida = selectedShift.horaFin;
          initialData.dias_laborales = selectedShift.diasLaborales || [];
        }
      }

      setFormData(initialData);

      console.log('📅 ContractForm - FormData establecido con fecha_inicio:', contract.fecha_inicio || 'EMPTY');
    }
  }, [contract, availableShifts]);

  // Cargar info del empleado seleccionado
  useEffect(() => {
    if (formData.employee_id && !isEditing) {
      const employee = employees.find(e => e.id === formData.employee_id);
      if (employee) {
        // Pre-llenar datos del empleado
        setFormData(prev => ({
          ...prev,
          position_id: employee.position_id || '',
          department_id: employee.department_id || '',
          salario: employee.salario || ''
        }));
      }
    }
  }, [formData.employee_id, employees, isEditing]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleDocumentoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tamaño (máx 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('El archivo es muy grande. Máximo 5MB.');
        return;
      }

      // Validar tipo
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        alert('Solo se permiten archivos PDF, DOC o DOCX.');
        return;
      }

      setDocumento(file);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.employee_id) {
      newErrors.employee_id = 'Debe seleccionar un empleado';
    }

    if (!formData.tipo_contrato) {
      newErrors.tipo_contrato = 'Debe seleccionar un tipo de contrato';
    }

    if (!formData.fecha_inicio) {
      newErrors.fecha_inicio = 'La fecha de inicio es requerida';
    }

    if (formData.tipo_contrato !== 'indefinido' && !formData.fecha_fin) {
      newErrors.fecha_fin = 'La fecha de fin es requerida para contratos a plazo fijo';
    }

    if (!formData.salario || parseFloat(formData.salario) <= 0) {
      newErrors.salario = 'El salario es requerido y debe ser mayor a 0';
    }

    if (formData.tiene_periodo_prueba) {
      if (!formData.duracion_prueba_dias || parseInt(formData.duracion_prueba_dias) <= 0) {
        newErrors.duracion_prueba_dias = 'La duración del período de prueba es requerida';
      }
      if (!formData.fecha_fin_prueba) {
        newErrors.fecha_fin_prueba = 'La fecha de fin del período de prueba es requerida';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      alert('Por favor corrige los errores del formulario');
      return;
    }

    const submitData = { ...formData };
    if (documento) {
      submitData.documento = documento;
    }

    let result;
    if (isEditing) {
      result = await updateContract(contract.id, submitData);
    } else {
      result = await createContract(submitData);
    }

    if (result.success) {
      alert(isEditing ? 'Contrato actualizado correctamente' : 'Contrato creado correctamente');
      onClose(true);
    } else {
      alert(result.error || 'Error al guardar el contrato');
    }
  };

  const selectedEmployee = employees.find(e => e.id === formData.employee_id);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-blue-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8" />
            <div>
              <h1 className="text-3xl font-bold">
                {isEditing ? 'Editar Contrato' : 'Nuevo Contrato'}
              </h1>
              <p className="text-blue-100 mt-1">
                {isEditing ? 'Modifica los datos del contrato' : 'Completa el formulario para crear un contrato laboral'}
              </p>
            </div>
          </div>
          <button
            onClick={() => onClose(false)}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Formulario */}
      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6 space-y-6">
          {/* Sección: Información del Empleado */}
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Información del Empleado
            </h2>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Empleado <span className="text-red-500">*</span>
                </label>
                <select
                  name="employee_id"
                  value={formData.employee_id}
                  onChange={handleChange}
                  disabled={isEditing}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.employee_id ? 'border-red-500' : 'border-gray-300'
                  } ${isEditing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">Seleccionar empleado</option>
                  {employees
                    .sort((a, b) => {
                      const nameA = `${a.nombre} ${a.apellido}`.toLowerCase();
                      const nameB = `${b.nombre} ${b.apellido}`.toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                    .map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.nombre} {emp.apellido} • {emp.employee_id} {emp.estado === 'inactivo' ? '(Inactivo)' : ''}
                      </option>
                    ))
                  }
                </select>
                {errors.employee_id && (
                  <p className="text-red-500 text-xs mt-1">{errors.employee_id}</p>
                )}
              </div>

              {selectedEmployee && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-blue-600 font-semibold">Cargo</p>
                      <p className="text-gray-700">{selectedEmployee.cargo || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-blue-600 font-semibold">Departamento</p>
                      <p className="text-gray-700">{selectedEmployee.departamento || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-blue-600 font-semibold">Salario Actual</p>
                      <p className="text-gray-700">
                        RD$ {parseFloat(selectedEmployee.salario || 0).toLocaleString('es-DO')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sección: Tipo de Contrato */}
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Tipo de Contrato
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tipo de Contrato <span className="text-red-500">*</span>
                </label>
                <select
                  name="tipo_contrato"
                  value={formData.tipo_contrato}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.tipo_contrato ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="indefinido">Indefinido</option>
                  <option value="plazo_fijo">Plazo Fijo</option>
                  <option value="temporal">Temporal</option>
                  <option value="por_obra">Por Obra</option>
                  <option value="prueba">Período de Prueba</option>
                </select>
                {errors.tipo_contrato && (
                  <p className="text-red-500 text-xs mt-1">{errors.tipo_contrato}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Número de Contrato
                </label>
                <input
                  type="text"
                  name="numero_contrato"
                  value={formData.numero_contrato}
                  onChange={handleChange}
                  placeholder="CT-2025-001"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Sección: Vigencia */}
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Vigencia del Contrato
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Fecha de Inicio <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="fecha_inicio"
                  value={formData.fecha_inicio}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.fecha_inicio ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.fecha_inicio && (
                  <p className="text-red-500 text-xs mt-1">{errors.fecha_inicio}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Fecha de Fin {formData.tipo_contrato !== 'indefinido' && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="date"
                  name="fecha_fin"
                  value={formData.fecha_fin}
                  onChange={handleChange}
                  disabled={formData.tipo_contrato === 'indefinido'}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.fecha_fin ? 'border-red-500' : 'border-gray-300'
                  } ${formData.tipo_contrato === 'indefinido' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {errors.fecha_fin && (
                  <p className="text-red-500 text-xs mt-1">{errors.fecha_fin}</p>
                )}
              </div>
            </div>
          </div>

          {/* Sección: Puesto y Remuneración */}
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
              Puesto y Remuneración
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Cargo
                </label>
                <select
                  name="position_id"
                  value={formData.position_id}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Seleccionar cargo</option>
                  {positions.map(pos => (
                    <option key={pos.id} value={pos.id}>
                      {pos.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Departamento
                </label>
                <select
                  name="department_id"
                  value={formData.department_id}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Seleccionar departamento</option>
                  {orgUnits.length > 0 ? (
                    <>
                      {orgUnits.map(unit => {
                        const unitDepts = departments.filter(d => d.organizational_unit_id === unit.id);
                        if (unitDepts.length === 0) return null;
                        return (
                          <optgroup key={unit.id} label={unit.nombre}>
                            {unitDepts.map(dept => (
                              <option key={dept.id} value={dept.id}>{dept.nombre}</option>
                            ))}
                          </optgroup>
                        );
                      })}
                      {departments.filter(d => !d.organizational_unit_id).length > 0 && (
                        <optgroup label="Sin unidad">
                          {departments.filter(d => !d.organizational_unit_id).map(dept => (
                            <option key={dept.id} value={dept.id}>{dept.nombre}</option>
                          ))}
                        </optgroup>
                      )}
                    </>
                  ) : (
                    departments.map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.nombre}</option>
                    ))
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Salario <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="salario"
                  value={formData.salario}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.salario ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.salario && (
                  <p className="text-red-500 text-xs mt-1">{errors.salario}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Moneda
                </label>
                <select
                  name="moneda"
                  value={formData.moneda}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="DOP">Peso Dominicano (DOP)</option>
                  <option value="USD">Dólar (USD)</option>
                  <option value="EUR">Euro (EUR)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Sección: Jornada Laboral */}
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Jornada Laboral
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tipo de Jornada
                </label>
                <select
                  name="tipo_jornada"
                  value={formData.tipo_jornada}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="completa">Jornada Completa</option>
                  <option value="parcial">Jornada Parcial</option>
                  <option value="por_horas">Por Horas</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Horas Semanales
                </label>
                <input
                  type="number"
                  name="horas_semanales"
                  value={formData.horas_semanales}
                  onChange={handleChange}
                  min="1"
                  max="168"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Sección: Turnos y Horarios */}
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Turnos y Horarios
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Turno Predefinido
                </label>
                <select
                  name="shift_id"
                  value={formData.shift_id}
                  onChange={(e) => {
                    const selectedShift = availableShifts.find(s => s.id === e.target.value);
                    if (selectedShift) {
                      setFormData(prev => ({
                        ...prev,
                        shift_id: e.target.value,
                        horario_entrada: selectedShift.horaInicio,
                        horario_salida: selectedShift.horaFin,
                        dias_laborales: selectedShift.diasLaborales || []
                      }));
                    } else {
                      handleChange(e);
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Sin turno asignado</option>
                  {availableShifts.map(shift => (
                    <option key={shift.id} value={shift.id}>
                      {shift.nombre} ({shift.horaInicio} - {shift.horaFin})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Horario de Entrada
                </label>
                <input
                  type="time"
                  name="horario_entrada"
                  value={formData.horario_entrada}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Horario de Salida
                </label>
                <input
                  type="time"
                  name="horario_salida"
                  value={formData.horario_salida}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Días laborales */}
            <div className="mt-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Días Laborales
              </label>
              <div className="flex flex-wrap gap-2">
                {['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'].map((dia) => (
                  <label
                    key={dia}
                    className={`px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                      (formData.dias_laborales || []).includes(dia.toLowerCase())
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={(formData.dias_laborales || []).includes(dia.toLowerCase())}
                      onChange={(e) => {
                        const diaLower = dia.toLowerCase();
                        setFormData(prev => ({
                          ...prev,
                          dias_laborales: e.target.checked
                            ? [...(prev.dias_laborales || []), diaLower]
                            : (prev.dias_laborales || []).filter(d => d !== diaLower)
                        }));
                      }}
                    />
                    <span className="text-sm font-semibold">{dia}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Sección: Período de Prueba */}
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600" />
              Período de Prueba
            </h2>
            <div className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="tiene_periodo_prueba"
                  checked={formData.tiene_periodo_prueba}
                  onChange={handleChange}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-semibold text-gray-700">
                  Este contrato tiene período de prueba
                </span>
              </label>

              {formData.tiene_periodo_prueba && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-8">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Duración (días) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="duracion_prueba_dias"
                      value={formData.duracion_prueba_dias}
                      onChange={handleChange}
                      min="1"
                      placeholder="90"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.duracion_prueba_dias ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.duracion_prueba_dias && (
                      <p className="text-red-500 text-xs mt-1">{errors.duracion_prueba_dias}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Fecha de Fin <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="fecha_fin_prueba"
                      value={formData.fecha_fin_prueba}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.fecha_fin_prueba ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.fecha_fin_prueba && (
                      <p className="text-red-500 text-xs mt-1">{errors.fecha_fin_prueba}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sección: Documento */}
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" />
              Documento del Contrato
            </h2>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Subir contrato firmado (PDF, DOC, DOCX - Máx 5MB)
              </label>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleDocumentoChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {documento && (
                <p className="text-sm text-green-600 mt-2">
                  ✓ Archivo seleccionado: {documento.name}
                </p>
              )}
            </div>
          </div>

          {/* Sección: Notas */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Notas Adicionales
            </label>
            <textarea
              name="notas"
              value={formData.notas}
              onChange={handleChange}
              rows={4}
              placeholder="Observaciones o condiciones especiales del contrato..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Renovación (solo para nuevos contratos) */}
          {!isEditing && selectedEmployee && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="es_renovacion"
                  checked={formData.es_renovacion}
                  onChange={handleChange}
                  className="w-5 h-5 text-amber-600 rounded focus:ring-amber-500"
                />
                <span className="text-sm font-semibold text-amber-900">
                  Este contrato es una renovación (finalizar contratos previos del empleado)
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Footer con botones */}
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3 rounded-b-xl border-t">
          <button
            type="button"
            onClick={() => onClose(false)}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-semibold"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditing ? 'Actualizar Contrato' : 'Crear Contrato'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContractForm;
